import React, { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import { SurveyTemplateEditor } from './SurveyCatalogTemplateEditPage'

function unwrap(res) {
  return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
}

export default function SurveyCustomCreatePage() {
  const navigate = useNavigate()
  const qc = useQueryClient()

  const catalogQuery = useQuery({
    queryKey: ['counsel-surveys', 'catalog'],
    queryFn: async () => unwrap(await api.get('/counsel-surveys/catalog', { __silent: true })),
  })

  const catalog = useMemo(
    () => (Array.isArray(catalogQuery.data) ? catalogQuery.data : []),
    [catalogQuery.data],
  )

  const fallbackCloneTemplateId = useMemo(() => {
    const common = catalog.find(
      (x) => x?.ready && x?.template?.id && String(x?.category?.code || '').toUpperCase() === 'COMMON',
    )
    if (common?.template?.id) return common.template.id
    const first = catalog.find((x) => x?.ready && x?.template?.id)
    return first?.template?.id ?? null
  }, [catalog])

  const catalogImportSources = useMemo(
    () => catalog
      .filter((x) => x?.ready && x?.template?.schema)
      .map((x) => ({
        code: x.category.code,
        categoryName: x.category.name,
        schema: x.template.schema,
      })),
    [catalog],
  )

  const saveMutation = useMutation({
    mutationFn: async ({ name, schema }) => {
      if (!fallbackCloneTemplateId) {
        throw new Error('운영 기본 설문 템플릿이 없어 맞춤 설문을 만들 수 없습니다. 운영자에게 문의해 주세요.')
      }
      const created = unwrap(await api.post(`/counsel-surveys/my-templates/from-template/${fallbackCloneTemplateId}`, {
        name: name || '내 맞춤 설문',
      }))
      return unwrap(await api.patch(`/counsel-surveys/my-templates/${created.id}`, {
        name: name || created.name,
        schema,
        publish: true,
        changeSummary: '맞춤 설문 생성',
      }))
    },
    onSuccess: async () => {
      try { window?.toast?.show?.('맞춤 설문을 저장했습니다.', 'success') } catch {}
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'catalog'] }),
        qc.invalidateQueries({ queryKey: ['counsel-surveys', 'my-templates', 'list'] }),
      ])
      navigate('/support/surveys?tab=list')
    },
    onError: (e) => {
      try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '저장 실패', 'error') } catch {}
    },
  })

  if (catalogQuery.isLoading) return <Loading />
  if (catalogQuery.isError) return <InlineError message="설문 목록을 불러오지 못했습니다." />

  if (!fallbackCloneTemplateId) {
    return (
      <div className="mx-auto max-w-lg px-4 py-12 text-center">
        <p className="text-slate-600">배포된 운영 설문 템플릿이 없어 맞춤 설문을 만들 수 없습니다.</p>
        <Link to="/support/surveys?tab=list" className="mt-4 inline-block rounded-lg border border-slate-200 px-4 py-2 text-sm hover:bg-slate-50">
          설문관리로
        </Link>
      </div>
    )
  }

  return (
    <div className="pb-10">
      <div className="mb-6">
        <div className="text-sm text-slate-500">
          <Link to="/support/surveys?tab=list" className="hover:text-brand-sky">설문관리</Link>
          <span className="mx-2 text-slate-300">/</span>
          <span className="text-slate-700">맞춤 설문 만들기</span>
        </div>
        <h1 className="mt-3 text-2xl font-bold text-slate-900">맞춤 설문 만들기</h1>
      </div>

      <SurveyTemplateEditor
        createMode
        item={null}
        baseSchema={null}
        catalogImportSources={catalogImportSources}
        onCancel={() => navigate('/support/surveys?tab=list')}
        onSave={(payload) => saveMutation.mutate(payload)}
        saving={saveMutation.isPending}
      />
    </div>
  )
}
